import React, { useState, useEffect } from 'react';
import apiService from '../../services/api/api.service';

const BalanceSheet = ({ uniqueTitle }) => {
  const [financials, setFinancials] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewType, setViewType] = useState('consolidated');

  useEffect(() => {
    const fetchBalanceSheet = async () => {
      try {
        setLoading(true);
        const result = await apiService.getProductByTitle(uniqueTitle);

        if (result?.success && result?.data?.financials) {
          setFinancials(result.data.financials);
        } else {
          throw new Error('No financial data available');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (uniqueTitle) {
      fetchBalanceSheet();
    }
  }, [uniqueTitle]);

  if (loading) 
    return (
      <div className="p-4 text-center text-gray-700 text-sm md:text-base">
        Loading financial data...
      </div>
    );
  if (error) 
    return (
      <div className="p-4 text-red-500 text-center text-sm md:text-base">
        Error: {error}
      </div>
    );
  if (!financials) 
    return (
      <div className="p-4 text-gray-500 text-center text-sm md:text-base">
        No financial data available
      </div>
    );

  const renderTable = (data, title) => (
    <div className="mb-6 max-w-full">
      <h4 className="p-2 sm:p-3 text-sm sm:text-base font-semibold text-center border border-gray-300">
        {title}
      </h4>
      <div className="overflow-x-auto">
        <table className="w-full text-xs sm:text-sm border-collapse border border-gray-300">
          <thead>
            <tr className="bg-gray-100">
              <th className="px-2 py-1 sm:px-3 sm:py-2 text-center border border-gray-300 sticky left-0 bg-gray-100">
                Date
              </th>
              {Object.keys(data).map((key, idx) =>
                key !== 'date' && key !== '_id' ? (
                  <th 
                    key={idx} 
                    className="px-2 py-1 sm:px-3 sm:py-2 text-center border border-gray-300 whitespace-nowrap"
                  >
                    {key.replace(/([A-Z])/g, ' $1')}
                  </th>
                ) : null
              )}
            </tr>
          </thead>
          <tbody>
            {data.date.map((date, index) => (
              <tr key={index} className="border border-gray-300">
                <td className="px-2 py-1 sm:px-3 sm:py-2 text-center border border-gray-300 sticky left-0 bg-white">
                  {date ? new Date(date).toDateString() : 'N/A'}
                </td>
                {Object.keys(data).map((key) =>
                  key !== 'date' && key !== '_id' ? (
                    <td 
                      key={key} 
                      className="px-2 py-1 sm:px-3 sm:py-2 text-center border border-gray-300"
                    >
                      {data[key][index] !== null ? data[key][index] : 'N/A'}
                    </td>
                  ) : null
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="p-4 sm:p-6 mb-6 max-w-5xl mx-auto">
      <h3 className="text-base sm:text-lg font-bold text-center mb-4">
        Profit & Loss Statement
      </h3>

      {/* Toggle Button */}
      <div className="flex flex-wrap justify-center gap-2 mb-4">
        <button
          className={`px-3 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm font-semibold rounded-lg transition-all ${
            viewType === 'consolidated' ? 'bg-blue-600 text-white' : 'text-gray-600 bg-gray-100 hover:bg-gray-200'
          }`}
          onClick={() => setViewType('consolidated')}
        >
          Consolidated
        </button>
        <button
          className={`px-3 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm font-semibold rounded-lg transition-all ${
            viewType === 'standalone' ? 'bg-blue-600 text-white' : 'text-gray-600 bg-gray-100 hover:bg-gray-200'
          }`}
          onClick={() => setViewType('standalone')}
        >
          Standalone
        </button>
      </div>

      {/* Show Table Based on Selected View */}
      {viewType === 'consolidated' && financials.profitAndLoss?.consolidated
        ? renderTable(financials.profitAndLoss.consolidated, 'Consolidated')
        : viewType === 'standalone' && financials.profitAndLoss?.standalone
        ? renderTable(financials.profitAndLoss.standalone, 'Standalone')
        : <p className="text-center text-gray-500 text-sm md:text-base">
            No data available for {viewType}
          </p>}
    </div>
  );
};

export default BalanceSheet;